'use client';

import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePartners, useUpdateJobStatus } from '@/hooks/usePreInspection';
import { PreInspectionApiError } from '@/lib/pre-inspection/api';
import {
  encodeAssigneeIntoNotes,
  isValidUuid,
  type AssignmentTransitionCode,
} from '@/lib/pre-inspection/assignment';

interface PolicyHolderLike {
  name: string;
  phone: string;
  email?: string | null;
  user_id?: string | null;
}

interface AssignmentActionModalProps {
  jobId: string;
  toStatusCode: AssignmentTransitionCode;
  policyHolder: PolicyHolderLike;
  onClose: () => void;
}

const TITLE_BY_TRANSITION: Record<AssignmentTransitionCode, string> = {
  assigned_to_franchisee: 'Assign to franchisee',
  assigned_to_field_executive: 'Assign to field executive',
  assigned_to_customer: 'Assign to customer',
};

export function AssignmentActionModal({
  jobId,
  toStatusCode,
  policyHolder,
  onClose,
}: AssignmentActionModalProps) {
  const titleId = useId();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [fieldAgentName, setFieldAgentName] = useState<string>('');
  const [freeText, setFreeText] = useState<string>('');

  const NOTES_MAX = 500;

  const partnersQuery = usePartners({
    enabled: toStatusCode === 'assigned_to_franchisee',
  });
  const mutation = useUpdateJobStatus(jobId, {
    onSuccess: () => onClose(),
  });

  const partners = partnersQuery.data ?? [];
  const selectedPartner = partners.find((p) => p.id === selectedPartnerId) ?? null;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (mutation.isPending) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mutation.isPending, onClose]);

  const trimmedFieldAgent = fieldAgentName.trim();

  const customerUuidInvalid =
    toStatusCode === 'assigned_to_customer' &&
    typeof policyHolder.user_id === 'string' &&
    policyHolder.user_id.length > 0 &&
    !isValidUuid(policyHolder.user_id);

  const canConfirm = (() => {
    if (mutation.isPending) return false;
    if (toStatusCode === 'assigned_to_franchisee') {
      return selectedPartner !== null;
    }
    if (toStatusCode === 'assigned_to_field_executive') {
      return trimmedFieldAgent.length > 0;
    }
    if (toStatusCode === 'assigned_to_customer') {
      return !customerUuidInvalid;
    }
    return false;
  })();

  function handleConfirm() {
    if (!canConfirm) return;
    if (toStatusCode === 'assigned_to_franchisee' && selectedPartner) {
      const notes = encodeAssigneeIntoNotes({
        kind: 'partner',
        display: selectedPartner.name,
        id: selectedPartner.id,
        freeText,
      });
      mutation.mutate({ status: toStatusCode, notes });
      return;
    }
    if (toStatusCode === 'assigned_to_field_executive') {
      const notes = encodeAssigneeIntoNotes({
        kind: 'field_agent',
        display: trimmedFieldAgent,
        freeText,
      });
      mutation.mutate({ status: toStatusCode, notes });
      return;
    }
    if (toStatusCode === 'assigned_to_customer') {
      const notes = encodeAssigneeIntoNotes({
        kind: 'customer',
        display: policyHolder.name,
        id:
          typeof policyHolder.user_id === 'string' && isValidUuid(policyHolder.user_id)
            ? policyHolder.user_id
            : null,
        freeText,
      });
      mutation.mutate({ status: toStatusCode, notes });
    }
  }

  const panel = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          {TITLE_BY_TRANSITION[toStatusCode]}
        </h2>

        {toStatusCode === 'assigned_to_customer' && (
          <div className="mt-4 space-y-2">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              <p className="font-medium text-gray-900">{policyHolder.name}</p>
              <p className="text-gray-600">{policyHolder.phone}</p>
              {policyHolder.email && (
                <p className="text-gray-600">{policyHolder.email}</p>
              )}
            </div>
            <p className="text-xs text-gray-500">
              The job will be assigned to the registered policy holder.
            </p>
            {customerUuidInvalid && (
              <p role="alert" className="text-sm text-red-700">
                Invalid policy holder user id.
              </p>
            )}
          </div>
        )}

        {toStatusCode === 'assigned_to_field_executive' && (
          <div className="mt-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Field executive</span>
              <input
                type="text"
                aria-label="Field executive"
                value={fieldAgentName}
                onChange={(e) => setFieldAgentName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                placeholder="e.g., John Doe (EMP-0042)"
              />
              <span className="mt-1 block text-xs text-gray-500">
                Enter the field executive&apos;s name. Optional: append{' '}
                <code>(EMP-xxxx)</code> for employee id. Backend dropdown coming in a future
                sprint.
              </span>
            </label>
          </div>
        )}

        {toStatusCode === 'assigned_to_franchisee' && (
          <div className="mt-4">
            {partnersQuery.isLoading && (
              <div
                data-testid="assignment-modal-partners-loading"
                className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500"
              >
                Loading partners…
              </div>
            )}
            {partnersQuery.isError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <p>
                  {partnersQuery.error instanceof PreInspectionApiError
                    ? partnersQuery.error.message
                    : 'Failed to load partners.'}
                </p>
                <button
                  type="button"
                  onClick={() => partnersQuery.refetch()}
                  className="mt-2 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                >
                  Retry
                </button>
              </div>
            )}
            {partnersQuery.isSuccess && partners.length === 0 && (
              <p className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                No partners available. Ask a platform admin to onboard one first.
              </p>
            )}
            {partnersQuery.isSuccess && partners.length > 0 && (
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Partner</span>
                <select
                  aria-label="Partner"
                  value={selectedPartnerId}
                  onChange={(e) => setSelectedPartnerId(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Select a partner…</option>
                  {partners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <div className="mt-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Notes (optional)</span>
            <textarea
              aria-label="Notes"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              maxLength={NOTES_MAX}
              rows={3}
              disabled={mutation.isPending}
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </label>
          <p
            data-testid="assignment-modal-notes-counter"
            aria-live="polite"
            className="mt-1 text-xs text-gray-500"
          >
            {freeText.length}/{NOTES_MAX}
          </p>
        </div>

        {mutation.isError && (
          <div
            role="alert"
            className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {mutation.error instanceof PreInspectionApiError
              ? mutation.error.message
              : 'Failed to update status.'}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {mutation.isPending ? 'Assigning…' : 'Confirm Assign'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(panel, document.body);
}
